/**
 * Offline queue utility using localStorage.
 * Queues failed POST/PATCH requests for retry when the connection returns.
 */

const QUEUE_KEY = "feridas_offline_queue";

export function getQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
}

export function enqueue(item) {
  const q = getQueue();
  q.push({ ...item, queued_at: new Date().toISOString(), id: crypto.randomUUID() });
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
}

export function dequeue(id) {
  const q = getQueue().filter((i) => i.id !== id);
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
}

export function clearQueue() {
  localStorage.removeItem(QUEUE_KEY);
}

/**
 * Processes the offline queue by retrying each item.
 * Returns { synced, failed } counts.
 */
export async function processQueue(axiosInstance) {
  const queue = getQueue();
  if (!queue.length) return { synced: 0, failed: 0 };

  let synced = 0;
  let failed = 0;

  for (const item of queue) {
    try {
      if (item.method === "POST") {
        await axiosInstance.post(item.url, item.data, { withCredentials: true });
      } else if (item.method === "PATCH") {
        await axiosInstance.patch(item.url, item.data, { withCredentials: true });
      }
      dequeue(item.id);
      synced++;
    } catch {
      failed++;
    }
  }
  return { synced, failed };
}

// --- Offline data cache (read cache) ---
const CACHE_KEY_PREFIX = "feridas_cache_";

export function cacheData(key, data) {
  try {
    localStorage.setItem(CACHE_KEY_PREFIX + key, JSON.stringify({ data, cached_at: Date.now() }));
  } catch {}
}

export function getCachedData(key, maxAgeMs = 24 * 60 * 60 * 1000) {
  try {
    const raw = localStorage.getItem(CACHE_KEY_PREFIX + key);
    if (!raw) return null;
    const { data, cached_at } = JSON.parse(raw);
    if (Date.now() - cached_at > maxAgeMs) return null;
    return data;
  } catch {
    return null;
  }
}
